/**
 * The CSV the Mobile Service Appointments tab exports: the columns, formatting, and filename
 * convention, in the shape of the Moves page export (`moves_dashboard_<from>_to_<to>`). One row
 * per appointment, in the order the tab shows them. Pure row assembly; fetching and file
 * generation live with the tab's hook.
 */

import dayjs from 'dayjs';

/** The fields a row needs. Structural, so both the page query's rows and the export query's rows fit. */
export interface ExportableAppointment {
  id: number;
  status?: string | null;
  source?: string | null;
  appointment_time?: string | null;
  consumer_name?: string | null;
  consumer_phone?: string | null;
  ro_number?: string | null;
  pickup?: { name?: string | null; address?: string | null } | null;
  vehicle_year?: number | string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_color?: string | null;
  vehicle_vin?: string | null;
  driver_notes?: string | null;
  createdat?: string | null;
  createdby?: string | null;
  service_orders?: Array<{
    stop?: { run?: { id: number; status?: string | null } | null } | null;
  }> | null;
}

/** The file's columns, in order. The keys become the header row, so this is the downstream contract. */
export const APPOINTMENT_CSV_COLUMNS = [
  'appointment_id',
  'status',
  'appointment_time',
  'consumer_name',
  'consumer_phone',
  'ro_number',
  'service_location',
  'service_address',
  'vehicle_year',
  'vehicle_make',
  'vehicle_model',
  'vehicle_color',
  'vehicle_vin',
  'formatted_vehicle',
  'run_id',
  'run_status',
  'source',
  'driver_notes',
  'created_at',
  'created_by',
] as const;

export type AppointmentCsvColumn = (typeof APPOINTMENT_CSV_COLUMNS)[number];

export type AppointmentCsvRow = Record<AppointmentCsvColumn, string | number>;

// The same labels the table's status chips show; statuses outside the known lifecycle fall back
// to a capitalized form of the raw value rather than an empty cell.
const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  pending: 'Pending',
  ready: 'Ready',
  canceled: 'Canceled',
  paying: 'Paying',
  paid: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
  completed: 'Completed',
  showed: 'Showed',
  missed: 'Missed',
  duplicate: 'Duplicate',
};

export const formatAppointmentStatus = (status?: string | null): string => {
  if (!status) return '';
  const key = status.toLowerCase();
  if (STATUS_LABELS[key]) return STATUS_LABELS[key];
  return key.charAt(0).toUpperCase() + key.slice(1);
};

const formatTime = (time?: string | null): string => (time ? dayjs(time).format('M/D/YYYY h:mm A') : '');

/**
 * Digits only, with a leading US country code dropped, so the cell matches a DMS export or a
 * lookup on the plain number. The portal's display formatter is not used here: it writes a
 * non-breaking space and a word joiner into the string, which no spreadsheet match survives.
 */
export const formatCsvPhone = (phone?: string | null): string => {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

/**
 * A cell that starts with a formula trigger is prefixed with an apostrophe, so a consumer name or
 * note like `=HYPERLINK(...)` opens as text in Excel and Sheets instead of running. Applied to
 * every string cell; numbers are written as numbers and never match.
 */
export const escapeCsvFormula = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

const escapeRow = (row: AppointmentCsvRow): AppointmentCsvRow =>
  Object.fromEntries(
    Object.entries(row).map(([key, value]) => [key, typeof value === 'string' ? escapeCsvFormula(value) : value])
  ) as AppointmentCsvRow;

const formatVehicle = (a: ExportableAppointment): string =>
  [a.vehicle_year, a.vehicle_color, a.vehicle_make, a.vehicle_model].filter(Boolean).join(' ');

/** Every distinct run the appointment is linked to through its service orders, first-seen order. */
const linkedRuns = (a: ExportableAppointment): Array<{ id: number; status: string }> => {
  const byId = new Map<number, { id: number; status: string }>();
  for (const order of a.service_orders ?? []) {
    const run = order?.stop?.run;
    if (!run || byId.has(run.id)) continue;
    byId.set(run.id, { id: run.id, status: run.status ?? '' });
  }
  return [...byId.values()];
};

/** Created-by is an email when a portal user made the appointment and a system tag otherwise; only the email is useful downstream. */
const formatCreatedBy = (createdby?: string | null): string => (createdby && createdby.includes('@') ? createdby : '');

export const buildAppointmentsCsvRows = (appointments: readonly ExportableAppointment[]): AppointmentCsvRow[] =>
  appointments.map(a => {
    const runs = linkedRuns(a);
    return escapeRow({
      appointment_id: a.id,
      status: formatAppointmentStatus(a.status),
      appointment_time: formatTime(a.appointment_time),
      consumer_name: a.consumer_name || '',
      consumer_phone: formatCsvPhone(a.consumer_phone),
      ro_number: a.ro_number || '',
      service_location: a.pickup?.name || '',
      service_address: a.pickup?.address || '',
      vehicle_year: a.vehicle_year ?? '',
      vehicle_make: a.vehicle_make || '',
      vehicle_model: a.vehicle_model || '',
      vehicle_color: a.vehicle_color || '',
      vehicle_vin: a.vehicle_vin || '',
      formatted_vehicle: formatVehicle(a),
      run_id: runs.map(r => r.id).join(', '),
      run_status: runs.map(r => r.status).join(', '),
      source: a.source || '',
      driver_notes: a.driver_notes || '',
      created_at: formatTime(a.createdat),
      created_by: formatCreatedBy(a.createdby),
    });
  });

/** The moves dashboard's filename convention, with this surface's own prefix. */
export const resolveAppointmentsCsvFilename = (startIso: string, endIso: string): string =>
  `mobile_service_appointments_${dayjs(startIso).format('MM-DD-YYYY')}_to_${dayjs(endIso).format('MM-DD-YYYY')}`;
